import { BaseEvent } from '../types/BaseEvent';
import { getResource, ResourceConfig, ResourceReportingConfig, EnvironmentReportingConfig } from '@shared/config';
import { AppError, AsyncResult, Environment, err, getEnvironment, ok, Result, tryCatch } from '@shared/utils';
import { ReportingService } from '../interfaces';
import { ConsoleService } from '../services/ConsoleService';
import { PostHogService } from '../services/PostHogService';
import { REPORTING_TRANSPORTS } from '@shared/config/src/constants/REPORTING_TRANSPORTS';
import { Logger } from '@shared/logger';

/**
 * @group Classes
 * @summary <blockquote>Main reporter class for handling analytics events with multiple services.</blockquote>
 * 
 * @example
 * ```typescript
 * import { Reporter } from '@shared/reporting';
 * 
 * const reporter = new Reporter({
 *   id: 'evt-001',
 *   distinctId: 'user-123',
 *   name: 'user:sign_in',
 *   props: { method: 'credentials' }
 * });
 * reporter.report(); // Fire-and-forget
 * ```
 */
/**
 * @internal
 */
export class Reporter {
  private config: ResourceReportingConfig | undefined;
  private currentEnv: Environment | undefined;
  private resourceConfig: ResourceConfig | null | undefined;
  private currentEnvConfig: EnvironmentReportingConfig | undefined;
  private event: BaseEvent;

  constructor(event: BaseEvent) {
    this.event = event;
    this.initializeConfig();
  }

  /**
   * @summary
   * <blockquote>Initialize configuration from ResourceConfig</blockquote>
   * 
   * @private
   * @throws Error if environment detection fails (called during construction)
   */
  // eslint-disable-next-line @template/result-pattern -- constructor helper; callers cannot handle Result during construction
  private initializeConfig(): void {
    this.resourceConfig = getResource();
    this.config = this.resourceConfig?.reporting;

    // Check current environment
    const envResult = getEnvironment();
    if (envResult.isErr) {
      // eslint-disable-next-line @template/no-error-throwing -- constructor helper cannot return Result
      throw new Error(`Environment detection failed: ${envResult.getFail().errorMessage}`);
    }
    this.currentEnv = envResult.getSuccess();

    // Get environment-specific config
    this.currentEnvConfig = this.config?.[this.currentEnv];
  }

  /**
   * @summary
   * <blockquote>Report an analytics event to all configured services</blockquote>
   * 
   * @returns AsyncResult<boolean, AppError> indicating success or failure
   * 
   * @private
   */
  async report(): AsyncResult<boolean, AppError> {
    // Check if reporting is configured for current environment
    if (!this.currentEnvConfig) {
      return ok(false); // No reporting configured for this environment
    }

    this.event.id = this.event?.id || `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const reporterResult = this.getReporter(this.event);

    if (reporterResult.isErr) {
      return err(reporterResult.getFail());
    }

    const reporter = reporterResult.value;
    // Fire-and-forget: call report() without awaiting
    // Handle errors properly to prevent unhandled promise rejections
    reporter.report()
      .then((result) => {
        // Check if the result is an error and log it
        result.match({
          ok: () => {
            // Success - no action needed for fire-and-forget
          },
          err: (error) => {
            Logger.error('Failed to report event', {
              eventId: this.event.id,
              eventName: this.event.name,
              errorCode: error.errorCode,
              errorMessage: error.errorMessage
            }, error as unknown as Error);
          }
        });
      })
      .catch((error) => {
        // Handle unexpected promise rejections
        Logger.error('Unexpected error during fire-and-forget reporting', {
          eventId: this.event.id,
          eventName: this.event.name,
          errorMessage: error instanceof Error ? error.message : String(error)
        }, error instanceof Error ? error : new Error(String(error)));
      });

    return ok(true);
  }

  /**
   * @summary
   * <blockquote>Determines which reporting service to use based on configuration and environment</blockquote>
   * 
   * @param event - The event to report (BaseEvent)
   * @returns Result<ReportingService, AppError> containing the appropriate service or error
   * 
   * @example
   * ```typescript
   * const event: BaseEvent = {
   *   id: 'evt-001',
   *   distinctId: 'user-123',
   *   name: 'user:sign_in',
   *   props: { method: 'credentials' }
   * };
   * 
   * const serviceResult = getReporter(event);
   * serviceResult.match({
   *   ok: (service) => {
   *     // Use the selected service
   *     console.log('Using service:', service.constructor.name);
   *   },
   *   err: (error) => console.error('Failed to get service:', error)
   * });
   * ```
   */
  private getReporter = (event: BaseEvent): Result<ReportingService, AppError> => {
    if (!this.currentEnvConfig) {
      // Wrap service creation in tryCatch to handle constructor errors
      return tryCatch(() => new ConsoleService(event));
    }

    const transport = this.currentEnvConfig.transport;

    switch (transport) {
      case REPORTING_TRANSPORTS.CONSOLE: {
        // Wrap service creation in tryCatch to handle constructor errors
        return tryCatch(() => new ConsoleService(event));
      }
      case REPORTING_TRANSPORTS.POSTHOG: {
        // Wrap service creation in tryCatch to handle constructor errors
        return tryCatch(() => new PostHogService(event));
      }
      default: {
        // Wrap service creation in tryCatch to handle constructor errors
        return tryCatch(() => new ConsoleService(event));
      }
    }
  }
} 